import { useMemo, useState, useCallback, useEffect } from 'react';

import { GwpHeader } from '../GwpHeader';
import { GwpFooter } from '../GwpFooter';
import { useViewModel } from './useViewModel';
import { GwpCard } from '../GwpCard';
import { SelectionProvider } from '../../context/SelectionContext.tsx';
import { useCart } from '../../context/CartContext';
import { GwpLoader } from '../GwpLoader';
import { GwpHelperModal } from '../GwpHelperModal';
import type { GwpProduct } from '../../utilities/types.tsx';

import styles from './gwp-modal.module.css';
import gridStyles from '../GwpStep/gwp-grid.module.css';
import cardStyles from '../GwpCard/gwp-card.module.css';

// Component-specific types
type SecondaryOption = {
  id: string;
  title: string;
  availableForSale: boolean;
  price: { amount: number; currencyCode?: string };
  compareAtPrice: { amount?: number; currencyCode?: string };
  image: { url: string | null };
  shortDescription: { value: string };
};

type GridItem = {
  id: string;
  cardId: string;
  title: string;
  image: { url: string | null };
  price: { amount: number; currencyCode: string };
  secondaryOptionsName: string;
  secondaryOptionsLabel: string;
  secondaryOptions: SecondaryOption[];
  disableSecondaryOptions: boolean;
  helpPage: { title: string; body: string; modalTriggerText: { value: string } } | null;
  product: { title: string };
  originalProduct: GwpProduct;
};

type GwpModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

/**
 * Gift With Purchase Modal.
 * Renders a grid of eligible gift products; supports variant selection and a Choose Gift action.
 *
 * - Shows grid when giftState is ELIGIBLE or CLAIMED.
 * - Shows empty state when GROWING.
 * - Should not be opened when UNAVAILABLE (caller responsibility).
 */
export function GwpModal({ isOpen, onClose }: GwpModalProps) {
  const { gwp } = useCart();
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [helperModalOpen, setHelperModalOpen] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<GwpProduct | null>(null);

  const { dialogModalRef, contentRef, handleClose } = useViewModel({ isOpen, onClose });

  // Initialize selection from claimed gift if present.
  useEffect(() => {
    if (gwp?.selectedGift?.variantId) {
      console.log('[GWP] Preselecting claimed variant', gwp.selectedGift.variantId);
      setSelectedVariantId(gwp.selectedGift.variantId);
    }
  }, [gwp?.selectedGift?.variantId]);

  const isSelectable = gwp.giftState === 'ELIGIBLE' || gwp.giftState === 'CLAIMED';

  /**
   * Handles opening the helper modal with product details.
   */
  const handleInfoClick = useCallback((product: GwpProduct) => {
    setSelectedProduct(product);
    setHelperModalOpen(true);
  }, []);

  /**
   * Handles closing the helper modal.
   */
  const handleHelperModalClose = useCallback(() => {
    setHelperModalOpen(false);
    setSelectedProduct(null);
  }, []);

  const gridItems = useMemo((): GridItem[] => {
    return (gwp.giftProducts || []).map((p) => {
      const variants = Array.isArray(p.variants) ? p.variants : [];
      const disableSecondaryOptions = variants.length <= 1;
      const singleVariantId = disableSecondaryOptions && variants[0]?.id ? variants[0].id : null;
      return {
        id: p.id,
        cardId: singleVariantId || p.id,
        title: p.title,
        image: { url: p.image },
        price: { amount: variants?.[0]?.price || 0, currencyCode: variants?.[0]?.currencyCode || 'GBP' },
        secondaryOptionsName: p.title,
        secondaryOptionsLabel: 'Choose an option',
        secondaryOptions: variants.map((v) => ({
          id: v.id,
          title: v.title,
          availableForSale: v.availableForSale,
          price: { amount: v.price, currencyCode: v.currencyCode },
          compareAtPrice: { amount: v.compareAtPrice, currencyCode: v.compareAtCurrencyCode },
          image: { url: v.image || p.image },
          shortDescription: { value: '' }
        })),
        disableSecondaryOptions,
        helpPage: p.descriptionHtml
          ? { title: p.title, body: p.descriptionHtml, modalTriggerText: { value: 'View details' } }
          : null,
        product: { title: p.title },
        originalProduct: p
      };
    });
  }, [gwp.giftProducts]);

  const handleChooseGift = useCallback(async () => {
    console.log('[GWP] Choose Gift clicked', { selectedVariantId });
    if (!selectedVariantId || isAdding) return;
    
    // If the same variant is already selected as gift, just close.
    if (gwp.selectedGift?.variantId === selectedVariantId) {
      console.log('[GWP] Variant already claimed, closing modal');
      handleClose();
      return;
    }

    const variant = gwp.giftProducts
      .find((product) => product.variants.some((variant) => variant.id === selectedVariantId))
      ?.variants?.find((variant) => variant.id === selectedVariantId);

    if (!variant) {
      console.error('[GWP] Variant not found', selectedVariantId);
      return;
    }

    // Properties are different between free and paid products
    let properties: Record<string, string> = {};
    if (variant.price === 0) {
      properties._freeGift = 'main';
      properties._vendorRoutingMatchFree = variant.vendor;
    } else {
      properties._promotional = 'true';
      properties._vendorRoutingMatch = variant.vendor;
    }

    try {
      setIsAdding(true);
      await gwp.chooseGift(selectedVariantId, properties);
      console.log('[GWP] Choose Gift success, closing modal');
      handleClose();
    } finally {
      setIsAdding(false);
    }
  }, [selectedVariantId, gwp, handleClose, isAdding]);

  const handleRemoveGift = useCallback(async () => {
    try {
      setIsAdding(true);
      // Clear selection immediately to visually deselect the card.
      setSelectedVariantId(null);
      await gwp.removeGift();
      // Close modal; drawer will be handled by context.
      handleClose();
    } finally {
      setIsAdding(false);
    }
  }, [gwp, handleClose]);

  const type = gwp.type;
  let modalTitle = '';
  switch (type) {
    case 'Spend X, choose a product (GWP)':
      modalTitle = 'Select a product';
      break;
    case 'Spend X, see collection link(s)':
      modalTitle = 'Select a category';
      break;
    case 'Spend X, see message':
      modalTitle = String(gwp?.page?.title || '');
      break;
  }

  return (
    <dialog ref={dialogModalRef} className={styles['personalisation__modal']}>
      <GwpHeader
        title={modalTitle}
        resetText={gwp.selectedGift ? 'Remove' : ''}
        onClose={handleClose}
        onReset={gwp.selectedGift ? handleRemoveGift : null}
        isResetting={isAdding}
      />
      <div ref={contentRef} className={styles['personalisation__content']}>
        {isSelectable && type.includes('GWP') && (
          <SelectionProvider
            selectedVariantId={selectedVariantId}
            onSelectVariant={(id: string) => {
              console.log('[GWP] Variant selected', id);
              setSelectedVariantId(id);
            }}
          >
            <fieldset>
              <ul
                className={
                  gridStyles['personalisation__selection-grid'] +
                  ' ' +
                  gridStyles['personalisation__selection-grid--2-column']
                }
              >
                {gridItems.map((item) => (
                  <li
                    key={item.id}
                    className={styles['personalisation__card-wrapper']}
                    aria-label={item.title || 'Gift option'}
                  >
                    {item.helpPage && (
                      <button
                        className={`${cardStyles['personalisation__card-page-helper']} button-reset`}
                        aria-label={`View details for ${item.title}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleInfoClick(item.originalProduct);
                        }}
                      >
                        <span className="svg-wrapper svg-wrapper--small">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M0.833374 8.00004C0.833374 4.042 4.042 0.833374 8.00004 0.833374C11.958 0.833374 15.1667 4.04199 15.1667 8.00004C15.1667 11.958 11.958 15.1667 8.00004 15.1667C4.04199 15.1667 0.833374 11.958 0.833374 8.00004ZM8.12997 6.68731C8.30704 6.71351 8.53917 6.78351 8.72904 6.99231C8.91064 7.19211 8.96297 7.42057 8.98291 7.58351C9.00017 7.72477 9.00011 7.89104 9.00004 8.04017V8.06671V11C9.00004 11.3682 8.70157 11.6667 8.33337 11.6667C7.96517 11.6667 7.66671 11.3682 7.66671 11V8.06671V8.00004C7.29851 8.00004 7.00004 7.70157 7.00004 7.33337C7.00004 6.96517 7.29851 6.66671 7.66671 6.66671L7.69391 6.6667C7.82604 6.66659 7.98904 6.66647 8.12997 6.68731ZM8.00004 4.66671C7.63184 4.66671 7.33337 4.96519 7.33337 5.33337C7.33337 5.70156 7.63184 6.00004 8.00004 6.00004C8.36824 6.00004 8.66677 5.70156 8.66677 5.33337C8.66677 4.96519 8.36824 4.66671 8.00004 4.66671Z"
                              fill="currentColor"
                            />
                          </svg>
                        </span>
                      </button>
                    )}
                    <GwpCard
                      key={item.id}
                      id={item.cardId}
                      slotId={'gift'}
                      imageSrc={item.image?.url}
                      imageAlt={item.title}
                      title={item.title}
                      description={''}
                      variants={[]}
                      price={item.price?.amount || 0}
                      secondaryOptions={item.secondaryOptions}
                      secondaryOptionsName={item.title}
                      secondaryOptionsLabel={item.secondaryOptionsLabel}
                      disableSecondaryOptions={item.disableSecondaryOptions}
                      selectedVariantId={selectedVariantId || ''}
                      isSelected={
                        selectedVariantId ? item.secondaryOptions.some((opt) => opt.id === selectedVariantId) : false
                      }
                      badge={undefined}
                      helpPage={undefined}
                      availableForSale={item.secondaryOptions.some((opt) => opt.availableForSale)}
                    />
                  </li>
                ))}
              </ul>
            </fieldset>
          </SelectionProvider>
        )}
        {type === 'Spend X, see message' && <div dangerouslySetInnerHTML={{ __html: gwp.page?.body ?? '' }}></div>}
        {type === 'Spend X, see collection link(s)' && gwp?.collections && (
          <fieldset>
            <ul
              className={
                gridStyles['personalisation__selection-grid'] +
                ' ' +
                gridStyles['personalisation__selection-grid--2-column']
              }
            >
              {gwp.collections.map((collection) => {
                return (
                  <a key={collection.id} href={`/collections/${collection.handle}`}>
                    <GwpCard
                      imageSrc={collection.image || ''}
                      imageAlt={collection.title}
                      availableForSale={true}
                      title={collection.title}
                      disableSecondaryOptions={true}
                    />
                  </a>
                );
              })}
            </ul>
          </fieldset>
        )}
      </div>

      <GwpFooter
        showAction={type.includes('GWP')}
        onClose={handleClose}
        onNext={handleChooseGift}
        isActionEnabled={!!selectedVariantId && !isAdding}
      />
      <GwpLoader isVisible={isAdding} />

      {/* Helper Modal for product details */}
      <GwpHelperModal isOpen={helperModalOpen} onClose={handleHelperModalClose} title={selectedProduct?.title || ''}>
        {selectedProduct?.descriptionHtml && (
          <div className="rte" dangerouslySetInnerHTML={{ __html: selectedProduct.descriptionHtml }} />
        )}
      </GwpHelperModal>
    </dialog>
  );
}